// Screen · the lesson page — "a map of that lesson right in front of them" (owner's vision,
// 2026-08-06). Route /map/:node (?class=<id> optional). Also the body of This Week.
//
// Top to bottom, in the order a teacher needs it: the lesson's name and one plain line; the files
// to open (worksheet, Teacher Pack, any required PDF) and "Plan this lesson"; then what to teach —
// book pages, aims, grammar, Cambridge vocabulary/practice, speaking & reading, school
// events — in plain words (no internal codes). The full resource list sits in one fold. Other weeks
// are one tap away (previous / next). Everything reads from the same static curriculum + materials
// index as before; nothing new is stored.

import { api } from "../api";
import { esc } from "../dom";
import { t } from "../i18n";
import { icon } from "../icons";
import { me } from "../state";
import { loadGrade, loadVocab, termNumber, type CurriculumNode, type GradeCurriculum } from "../curriculum";
import { materialsFor, resourcesFor, type NodeMaterials, type NodeResources, type Resource } from "../materials";
import {
  aimsOf, bookLine, cleanTitle, weekChips, gradeLine, nodeWhen, packPages, plainText, taskLabel, wordsOf, type ClassRow, type TermInfo,
} from "../lesson-data";
import { pageHeader, statusChip, bestState, fileButton, emptyState, type PlanState } from "../ui";
import { booksCardHtml, coursebookTarget, loadLessonRefs, readerButton, teacherTitle, type LessonRef } from "../lesson-refs";
import { glanceFor, type PackGlance } from "../library";
import { registerTarget } from "../book-reader";
import { materialGuideCatalogue, type MaterialGuideState } from "../materials-engine";
import "../styles/teach.css";
import { loadWeeklyPreparation, weeklyQuery, weeklyHref, type WeeklyContext, type WeeklyPreparation } from "../weekly-preparation";

export interface ClassLessonRow {
  lesson_id: string;
  plan_id: string | null;
  week_no: number;
  topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  node_id: string | null;
}

/** The grade number a node id belongs to ("G10-T2-04" → 10). */
export function gradeOfNode(nodeId: string): number | null {
  const m = /^G(\d{1,2})-/.exec(nodeId);
  return m ? Number(m[1]) : null;
}

/** Where a class lesson's plan opens: a draft goes to its editor, a sent plan to its page. */
export function lessonHref(l: ClassLessonRow): string {
  if (l.status === "in_progress" || l.status === "not_started") return `/lesson/${encodeURIComponent(l.lesson_id)}/edit`;
  return l.plan_id ? `/plan/${encodeURIComponent(l.plan_id)}` : `/lesson/${encodeURIComponent(l.lesson_id)}`;
}

/** The files to open for a node — big buttons, each opening in its own tab. */
export function filesHtml(mat: NodeMaterials | null, res: NodeResources | null): string {
  const req = (res?.items ?? []).filter((r) => r.required && r.href);
  const btns = [
    mat?.sheet ? fileButton(mat.sheet, t("lesson.worksheet"), "sheet", ' data-file="sheet"') : "",
    mat?.pack ? fileButton(mat.pack, t("lesson.pack"), "pack", ' data-file="pack"') : "",
    ...req.map((r) => fileButton(r.href!, cleanTitle(r.title), "pdf", ' data-file="required"')),
  ].filter(Boolean);
  return btns.join("");
}

function fact(label: string, body: string, cls = ""): string {
  return body ? `<div class="fact${cls ? " " + cls : ""}"><dt>${esc(label)}</dt><dd>${body}</dd></div>` : "";
}

/** "What to teach" — the node's content in plain words. `inFold`: drawn inside This Week's "More for
 *  planning" fold (no card of its own, and the page's .nodecard hook sits on the lesson card above). */
export function whatToTeachHtml(cur: GradeCurriculum | null, node: CurriculumNode, mat: NodeMaterials | null, reg: Awaited<ReturnType<typeof loadVocab>>, inFold = false, opts: { slim?: boolean; title?: string } = {}): string {
  // `slim`: the "How to teach" card above already says what the book pages and the week's aims are — this card
  // then keeps only what it adds (the grammar, the Cambridge words, the exam practice, speaking & reading, school events).
  const aims = opts.slim ? [] : aimsOf(node);
  const words = wordsOf(reg, node.thread);
  const task = taskLabel(node.thread?.task_family, node.thread?.bloom);
  const flu = node.fluency ? [node.fluency.activity, node.fluency.topic].filter(Boolean).join(" — ") : "";
  // What the week carries at school: the school calendar's own events for this term and week, plus the year plan's
  // department tags (owner ruling A2) — the typed tags for facts the calendar owns are no longer printed.
  const events = weekChips(node, cur?.grade ?? null);
  // A set with no words of its own (an orientation or exam-practice slot) is not a word list: the row
  // is left off rather than printing the slot's internal name. A review week says only that it is one.
  // Long sets (a Blueprint week lists 40+) show the first sixteen; the rest sit one tap away, so the
  // phone page does not open on a wall of chips.
  const chips = (list: string[]): string => `<ul class="word-chips">${list.map((w) => `<li>${esc(w)}</li>`).join("")}</ul>`;
  const uniqueWords = [...new Set(words?.words ?? [])];
  const wordsBody = words
    ? uniqueWords.length
      ? `<p class="words-title">${esc(words.title)} <span class="muted">· All ${uniqueWords.length} words shown</span></p>
         ${chips(uniqueWords)}`
      : words.review ? `<p class="words-title">${esc(words.title)}</p>` : ""
    : "";
  // "Teacher Pack, page 3" — where the pack explains this part of the week (opens the PDF at that page).
  const packLink = (note: string | null | undefined): string => {
    const pages = packPages(note);
    if (!pages.length || !mat?.pack) return "";
    const label = pages.length === 1 ? `Teacher Pack, page ${pages[0]}` : `Teacher Pack, pages ${pages[0]}–${pages[pages.length - 1]}`;
    return `<p class="fact-note"><a href="${esc(mat.pack)}#page=${pages[0]}" target="_blank" rel="noopener">${icon("pack", "ico-sm")} ${label}</a></p>`;
  };
  const body = [
    opts.slim ? "" : fact("In the book", esc(mat?.coursebook_note ? plainText(mat.coursebook_note) : bookLine(cur, node))),
    fact("Aims", aims.length ? `<ul class="aims">${aims.map((a) => `<li>${esc(a)}</li>`).join("")}</ul>` : ""),
    fact("Grammar", esc(node.coursebook?.grammar ?? "")),
    fact("Coursebook vocabulary topic", esc(node.coursebook?.vocabulary ?? "")),
    fact("Cambridge vocabulary", wordsBody, "fact-words"),
    fact("Cambridge practice", task ? esc(task) + packLink(mat?.thread_note) : ""),
    fact("Speaking & reading", flu ? esc(flu) + packLink(mat?.fluency_note) : ""),
    fact("At school", events.map((e) => `<span class="tag"${e.when ? ` title="${esc(e.when)}"` : ""}>${esc(e.label)}</span>`).join(" ")),
  ].join("");
  if (inFold) {
    return `<section class="lesson-facts in-fold">
    <h3 class="sub-title">What to teach</h3>
    <dl class="facts">${body || `<p class="muted">The details for this week are not written yet.</p>`}</dl>
  </section>`;
  }
  return `<section class="card lesson-facts nodecard" data-node-id="${esc(node.id)}">
    <h2 class="card-title">${esc(opts.title ?? "What to teach")}</h2>
    <dl class="facts">${body || `<p class="muted">The details for this week are not written yet.</p>`}</dl>
  </section>`;
}

const KIND_GROUP: Record<Resource["kind"], string> = {
  pack: "Files", sheet: "Files", reference: "Books & pages", audio: "Audio", practice: "Practice",
};

// The citation graph's own edge words ("teaches", "supports", "rail", "core" …) are build vocabulary,
// not a teacher's: say what the item is for instead.
const DETAIL_WORDS: [RegExp, string][] = [
  [/^(core|teaches)\b/i, "Student Book"],
  [/^teacher[_ ]guide\b/i, "Teacher's Guide"],
  [/^rail\b/i, "Phonics"],
  [/^(supports|extension|practised_by|exam_practice)\b/i, "Extra practice"],
  [/^homework\b/i, "Workbook"],
];
/** A resource row's small grey line in plain words: "teaches · p.4–5" → "Student Book · p.4–5". */
export function plainDetail(detail: string | undefined): string {
  if (!detail) return "";
  let d = cleanTitle(detail).replace(/\s*\(printed page not confirmed\)/gi, "");
  for (const [rx, word] of DETAIL_WORDS) if (rx.test(d)) { d = d.replace(rx, word); break; }
  return d;
}

// "LG2-SB" → "Let's Go 2" — only to say whose class audio a row is (the key carries the book id).
function audioBook(id: string): string {
  let m: RegExpExecArray | null;
  if ((m = /^LGB(\d)-SB$/.exec(id))) return `Let's Begin ${m[1]}`;
  if ((m = /^LG(\d)-SB$/.exec(id))) return `Let's Go ${m[1]}`;
  if ((m = /^OPW(\d)-SB$/.exec(id))) return `Oxford Phonics World ${m[1]}`;
  if ((m = /^BP(\d)-/.exec(id))) return `Blueprint ${m[1]}`;
  return "";
}
/** "LG5e_LG2_CD1_03.mp3, …" → "CD 1, from track 3" — where to start the class CD, not file names. */
export function plainAudio(detail: string | undefined): string {
  const first = (detail ?? "").split(/,\s*/)[0] ?? "";
  let m = /CD\s*_?(\d+)[ _]+(?:TRACK[ _]?)?(\d{1,3})\.mp3$/i.exec(first);
  if (m) return `CD ${Number(m[1])}, from track ${Number(m[2])}`;
  m = /track\s*_?(\d{1,3})\.mp3$/i.exec(first);
  return m ? `From track ${Number(m[1])}` : "";
}

/** The fold under the books card. With the card (ref) it carries only what the card does not: the
 *  short "More practice" list the build picked (precision first — never a second grammar unit or a
 *  whole book), the publisher's practice worksheets, the class audio and any printable file. Without
 *  the card (a stale build) it lists every item, as before. */
export function allResourcesHtml(res: NodeResources | null, ref: LessonRef | null = null): string {
  if (!ref) return allResourcesRawHtml(res);
  const items = (res?.items ?? []).filter((r) => r.kind !== "pack" && r.kind !== "sheet");
  const extras = ref.x ?? [];
  const groups: { name: string; rows: string[] }[] = [];
  if (extras.length) {
    groups.push({ name: "More practice", rows: extras.map((x) => `<li>${icon("book")}<span class="res-body"><span><b>${esc(x.book)}</b>${x.part ? ` · ${esc(x.part)}` : ""}${x.pages ? ` — ${esc(/[–,]/.test(x.pages) ? `pages ${x.pages}` : `page ${x.pages}`)}` : ""}</span><span class="res-detail">${esc(x.why)}</span>${readerButton(x, x.book, x.part ?? "More practice", null, "res-open")}</span></li>`) });
  }
  const seen = new Set<string>();
  const sheets = items.filter((r) => r.kind === "practice").filter((r) => {
    const k = `${r.title}|${r.detail ?? ""}`;
    return seen.has(k) ? false : (seen.add(k), true);
  });
  if (sheets.length) {
    groups.push({ name: "Worksheets", rows: sheets.map((r) => {
      const title = esc(cleanTitle(r.title));
      const pd = plainDetail(r.detail);
      return `<li>${icon("puzzle")}<span class="res-body">${r.href ? `<a href="${esc(r.href)}" target="_blank" rel="noopener">${title}</a>` : `<span>${title}</span>`}${pd ? `<span class="res-detail">${esc(pd)}</span>` : ""}</span></li>`;
    }) });
  }
  // the same CD list is cited once per taught lesson — one row per list, naming the lessons it serves
  const audio = new Map<string, { book: string; lessons: string[]; title: string; where: string }>();
  for (const r of items.filter((x) => x.kind === "audio")) {
    const parts = (r.key ?? "").split(":");
    const lesson = (parts[5] ?? "").replace(/@.*$/, "").replace(/-s-/g, "'s ").replace(/-/g, " ").trim();
    const k = r.detail ?? r.title;
    const a = audio.get(k) ?? { book: audioBook(parts[3] ?? ""), lessons: [], title: cleanTitle(r.title).replace(/ audio tracks?$/, " tracks"), where: plainAudio(r.detail) };
    if (lesson && !a.lessons.includes(lesson)) a.lessons.push(lesson);
    audio.set(k, a);
  }
  if (audio.size) {
    groups.push({ name: "Class audio", rows: [...audio.values()].map((a) => `<li>${icon("audio")}<span class="res-body"><span>${esc([a.book, a.lessons.join(", ")].filter(Boolean).join(" · "))} — ${esc(a.title)}</span>${a.where ? `<span class="res-detail">${esc(a.where)}</span>` : ""}</span></li>`) });
  }
  const files = items.filter((r) => r.href && r.kind !== "practice" && r.kind !== "audio");
  if (files.length) {
    groups.push({ name: "Files", rows: files.map((r) => `<li>${icon("pdf")}<span class="res-body"><a href="${esc(r.href!)}" target="_blank" rel="noopener">${esc(cleanTitle(r.title))}</a></span></li>`) });
  }
  const count = groups.reduce((n, g) => n + g.rows.length, 0);
  if (!count) return "";
  const perm = (res?.items ?? []).find((r) => r.permission)?.permission;
  return `<details class="card fold" id="all-resources">
    <summary>${icon("book")}<span>More practice and audio (optional) <span class="count">${count}</span></span>${icon("down", "ico-sm fold-caret")}</summary>
    <div class="fold-body">${perm ? `<p class="muted small">${esc(perm)}</p>` : ""}${groups.map((g) => `<h3 class="res-group">${esc(g.name)}</h3><ul class="res-list">${g.rows.join("")}</ul>`).join("")}</div>
  </details>`;
}

/** Every resource the week has, grouped, titles cleaned — one fold, closed by default. */
function allResourcesRawHtml(res: NodeResources | null): string {
  const seen = new Set<string>();
  const rows = (res?.items ?? []).filter((r) => {
    if (r.kind === "pack" || r.kind === "sheet") return false;
    // the same audio list is cited once per taught lesson — show it once
    const k = `${r.kind}|${r.title}|${r.detail ?? ""}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (!rows.length) return "";
  const groups = new Map<string, Resource[]>();
  for (const r of rows) {
    const g = KIND_GROUP[r.kind] ?? "Other";
    (groups.get(g) ?? groups.set(g, []).get(g)!).push(r);
  }
  const html = [...groups.entries()].map(([g, list]) => `<h3 class="res-group">${esc(g)}</h3><ul class="res-list">${list.map((r) => {
    const title = esc(cleanTitle(r.title).replace(/\s*\(printed page not confirmed\)/gi, ""));
    const pd = plainDetail(r.detail);
    const detail = pd ? `<span class="res-detail">${esc(pd)}</span>` : "";
    const ic = r.kind === "audio" ? "audio" : r.kind === "practice" ? "puzzle" : "book";
    return `<li>${icon(ic)}<span class="res-body">${r.href ? `<a href="${esc(r.href)}" target="_blank" rel="noopener">${title}</a>` : `<span>${title}</span>`}${detail}</span></li>`;
  }).join("")}</ul>`).join("");
  const perm = rows.find((r) => r.permission)?.permission;
  return `<details class="card fold" id="all-resources">
    <summary>${icon("book")}<span>More books and practice (optional) <span class="count">${rows.length}</span></span>${icon("down", "ico-sm fold-caret")}</summary>
    <div class="fold-body">${perm ? `<p class="muted small">${esc(perm)}</p>` : ""}${html}</div>
  </details>`;
}

/** Previous / next week links within the grade's year plan. */
export function prevNextHtml(cur: GradeCurriculum, node: CurriculumNode, classId: string | null, refs: Record<string, LessonRef> | null = null): string {
  const idx = cur.nodes.findIndex((n) => n.id === node.id);
  const q = classId ? `?class=${encodeURIComponent(classId)}` : "";
  const link = (n: CurriculumNode | undefined, dir: "prev" | "next"): string => n
    ? `<a class="pn pn-${dir}" data-link href="/map/${encodeURIComponent(n.id)}${q}">
        ${dir === "prev" ? icon("back") : ""}<span><span class="pn-when">${esc(nodeWhen(n))}</span><span class="pn-title">${esc(teacherTitle(n, refs?.[n.id]))}</span></span>${dir === "next" ? icon("chevron") : ""}
      </a>`
    : `<span class="pn pn-${dir} pn-none"></span>`;
  return `<nav class="prevnext" aria-label="Other weeks">${link(cur.nodes[idx - 1], "prev")}${link(cur.nodes[idx + 1], "next")}</nav>`;
}

/** The plan form's address for a lesson of the year plan: the class, the lesson and — when this page knows it — the school
 *  term and week, so the form opens on the week the teacher came from (owner ruling A3). */
export function planStartHref(classId: string, nodeId: string, when: { termId: string; weekNo: number } | null): string {
  const q = new URLSearchParams({ class: classId, node: nodeId });
  if (when) {
    q.set("term", when.termId);
    q.set("week", String(when.weekNo));
  }
  return `/plan/start?${q}`;
}

/** The plans this class already has for this lesson, with the next action. */
export function plansPanelHtml(cls: ClassRow | null, node: CurriculumNode, lessons: ClassLessonRow[], canPlan: boolean, byOthers: Set<string> = new Set(), when: { termId: string; weekNo: number } | null = null): string {
  if (!cls) {
    return `<section class="panel"><h2 class="panel-title">${icon("plans")}Plans</h2>
      <p class="muted small">Open this week from one of your classes to see its plans.</p></section>`;
  }
  const mine = lessons.filter((l) => l.node_id === node.id);
  // A plan the class's previous teacher wrote (the Lead moved the class) is listed, not linked: it opens
  // for its writer and the Lead only.
  const rows = mine.map((l) => byOthers.has(l.lesson_id)
    ? `<div class="row-link static" data-planned-by-other>
        <span class="rl-main">${esc(l.topic)} <span class="muted">· by the class's previous teacher</span></span><span class="rl-meta">${statusChip(l.status)}</span></div>`
    : `<a class="row-link" data-link href="${esc(lessonHref(l))}">
      <span class="rl-main">${esc(l.topic)}</span><span class="rl-meta">${statusChip(l.status)}</span>${icon("chevron", "ico-sm")}</a>`).join("");
  const ownCount = mine.filter((l) => !byOthers.has(l.lesson_id)).length;
  const plan = canPlan
    ? `<a class="btn${mine.length ? " secondary" : ""}" data-link href="${esc(planStartHref(cls.id, node.id, when))}">${icon("pencil")}<span>${ownCount ? "Plan another lesson" : mine.length ? "Plan your own" : t("lesson.plan")}</span></a>`
    : "";
  return `<section class="panel" aria-labelledby="pp-h">
    <h2 class="panel-title" id="pp-h">${icon("plans")}${esc(cls.name)} plans for this week</h2>
    ${rows ? `<div class="list">${rows}</div>` : `<p class="muted small">No plan yet for this week.</p>`}
    ${plan}
  </section>`;
}

export interface LessonView {
  cur: GradeCurriculum;
  node: CurriculumNode;
  mat: NodeMaterials | null;
  res: NodeResources | null;
  reg: Awaited<ReturnType<typeof loadVocab>>;
}

/** Load everything a lesson page shows for one node. Null when the node is unknown. */
export async function loadLessonView(nodeId: string): Promise<LessonView | null> {
  const grade = gradeOfNode(nodeId);
  if (!grade) return null;
  const cur = await loadGrade(grade);
  const node = cur?.nodes.find((n) => n.id === nodeId) ?? null;
  if (!cur || !node) return null;
  const [mat, res, reg] = await Promise.all([materialsFor(node.id), resourcesFor(node.id), loadVocab(grade)]);
  return { cur, node, mat, res, reg };
}

const STRAND_NAME: Record<PackGlance["strands"][number]["key"], string> = { coursebook: "Coursebook", thread: "Cambridge exam practice", fluency: "Speaking & reading" };

/** Teacher text, cleaned of build codes (plainText), escaped, and with every "(Teacher Pack p. 3)" pointer turned into
 *  a link that opens the Teacher Pack at that page inside EDEN (src/doc-viewer.ts), never in an outside PDF program. */
export function teacherTextHtml(raw: string | null | undefined, packHref: string | null | undefined): string {
  const html = esc(plainText(raw));
  if (!packHref) return html.replace(/\s*\(Teacher Pack pp?\. \d+(?:–\d+)?\)/g, "");
  return html.replace(/\(Teacher Pack (pp?)\. (\d+)(?:–(\d+))?\)/g, (_m, _p: string, a: string, b?: string) =>
    `<a class="pack-pg" href="${esc(packHref)}#page=${a}" data-doc-title="Teacher Pack">${icon("pack", "ico-sm")}Teacher Pack ${b ? `pages ${a}–${b}` : `page ${a}`}</a>`);
}

/** T1 review (owner, 9 Oct 2026: the pack is "just words and very hard to read") and the teacher review of 15 Oct
 *  ("like it is for a computer to read"): the Teacher Pack as one screen in a teacher's words — the week's goal, how the
 *  periods are shared, the steps OPEN (no tapping five times), and the question to think about. Generated from the
 *  pack itself (scripts/build-library.mjs) and cleaned of build codes by plainText; the full PDF stays one tap away. */
export function glanceHtml(g: PackGlance | null, packHref: string | null = null, opts: { bare?: boolean; title?: string } = {}): string {
  if (!g || (!g.objectives && !g.steps.length)) return "";
  const strands = g.strands.filter((x) => x.note).map((x) => `<div class="glance-strand"><b>${esc(STRAND_NAME[x.key])}${
    x.periods ? `<span>${x.periods} ${x.periods === 1 ? "period" : "periods"}</span>` : ""}</b><p>${teacherTextHtml(x.note, packHref)}</p></div>`).join("");
  const steps = g.steps.map((x, i) => `<li class="glance-step"><span class="gs-n" aria-hidden="true">${i + 1}</span><div><b>${esc(plainText(x.label))}</b><p>${teacherTextHtml(x.text, packHref)}</p></div></li>`).join("");
  const body = `
    ${g.objectives ? `<p class="glance-goals"><b>By the end of the week</b>${teacherTextHtml(g.objectives, packHref)}</p>` : ""}
    ${steps ? `<h3 class="sub-title glance-h">The lesson, step by step</h3><ol class="glance-steps open" aria-label="The lesson, step by step">${steps}</ol>` : ""}
    ${strands ? `<h3 class="sub-title glance-h">How the week's periods are shared</h3><div class="glance-strands">${strands}</div>` : ""}
    ${g.question ? `<p class="glance-q"><b>Think about:</b> ${teacherTextHtml(g.question, packHref)}</p>` : ""}`;
  if (opts.bare) return `<div class="glance bare" data-glance>${body}</div>`;
  return `<section class="card glance" aria-labelledby="gl-h" data-glance id="how-to-teach">
    <h2 class="card-title" id="gl-h">${esc(opts.title ?? "How to teach this week")}</h2>
    ${body}
  </section>`;
}

/** The readable teacher guide's link words, by what that week's guide holds; null = nothing worth opening (a guide
 *  "awaiting source review" or "not found" only says so). The same words on the lesson page and in the plan's panel. */
export function guideLinkLabel(state: MaterialGuideState | null | undefined): string | null {
  return state === "complete_guide" ? "Read the full teacher guide"
    : state === "activities_only" ? "Read the classroom activities"
    : state === "original_only" ? "See the original teaching notes"
    : null;
}

/** "Start with these" (teacher review, 15 Oct 2026 — many resources at once felt overwhelming): the three things a
 *  teacher needs for this lesson, in the order they use them — the Student Book pages (with the Teacher's Guide notes
 *  beside them), the worksheet, and how to teach it (the steps, the Teacher Pack, or the readable guide — whichever the
 *  week has). Everything else on the page is optional and folded. */
export function essentialsHtml(node: CurriculumNode, mat: NodeMaterials | null, ref: LessonRef | null, glance: PackGlance | null, classId: string | null,
  guide: { label: string; href: string } | null = null): string {
  const cb = coursebookTarget(ref);
  const q = classId ? `?class=${encodeURIComponent(classId)}` : "";
  const rows: string[] = [];
  if (cb) {
    rows.push(`<li class="es-row">
      <span class="es-n" aria-hidden="true">1</span>
      <div class="es-body"><b>Student Book</b><span>${esc(cb.book.replace(/ Student Book$/, ""))}${cb.unit ? ` · ${esc(cb.unit)}` : ""}${cb.pagesLabel ? ` · ${esc(cb.pagesLabel)}` : ""}</span>
        <span class="es-why">Opens the real pages with the Teacher's Guide notes beside them.</span></div>
      <button type="button" class="btn secondary sm" data-book-open="${registerTarget(cb.target)}">${icon("book", "ico-sm")}<span>Open the pages</span></button>
    </li>`);
  }
  if (mat?.sheet) {
    rows.push(`<li class="es-row">
      <span class="es-n" aria-hidden="true">${rows.length + 1}</span>
      <div class="es-body"><b>Worksheet</b><span>This week's practice for your class.</span>
        <span class="es-why">Look at it here, or make a print-ready copy with your school's heading.</span></div>
      <span class="es-btns"><a class="btn secondary sm" href="${esc(mat.sheet)}" data-doc-title="${esc(teacherTitle(node, ref))}">${icon("eye", "ico-sm")}<span>Look at it</span></a>
      <a class="btn secondary sm" data-link data-make-worksheet href="/materials-studio/${encodeURIComponent(node.id)}${q}">${icon("print", "ico-sm")}<span>Make &amp; print</span></a></span>
    </li>`);
  }
  // How to teach it: the Teacher Pack's steps when the week has them (read below); else the readable guide (the
  // Grade 7–10 weeks carry classroom activities, not pack steps); the full Teacher Pack whenever there is one.
  const steps = glance?.steps.length ?? 0;
  if (steps || mat?.pack || guide) {
    const what = steps ? `${steps} steps, from the Teacher Pack, in plain words.`
      : guide ? (guide.label === "Read the classroom activities" ? "The classroom activities from the teacher guide."
        : guide.label === "See the original teaching notes" ? "The original teaching notes for this week."
        : "The teacher guide for this week, in plain words.")
      : "The Teacher Pack for this week.";
    const why = steps ? `Read them below${mat?.pack ? " — or open the full Teacher Pack" : ""}.`
      : guide ? `Open it here${mat?.pack ? ", or open the full Teacher Pack" : ""}.`
      : "It opens here, inside EDEN.";
    rows.push(`<li class="es-row">
      <span class="es-n" aria-hidden="true">${rows.length + 1}</span>
      <div class="es-body"><b>How to teach it</b><span>${what}</span>
        <span class="es-why">${why}</span></div>
      <span class="es-btns">${steps ? `<a class="btn secondary sm" href="#how-to-teach" data-jump="how-to-teach">${icon("down", "ico-sm")}<span>See the steps</span></a>` : ""}
      ${!steps && guide ? `<a class="btn secondary sm" data-link href="${esc(guide.href)}">${icon("check", "ico-sm")}<span>${esc(guide.label)}</span></a>` : ""}
      ${mat?.pack ? `<a class="btn secondary sm" href="${esc(mat.pack)}" data-doc-title="Teacher Pack · ${esc(nodeWhen(node))}">${icon("pack", "ico-sm")}<span>Teacher Pack</span></a>` : ""}</span>
    </li>`);
  }
  if (!rows.length) return "";
  return `<section class="card essentials" aria-labelledby="es-h">
    <h2 class="card-title" id="es-h">Start with these</h2>
    <p class="muted small es-lead">Everything else on this page is extra — use it only if you need it.</p>
    <ol class="es-list">${rows.join("")}</ol>
  </section>`;
}

export async function renderLessonMap(page: HTMLElement, params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const nodeId = params.node ?? "";
  const classId = query.get("class");
  const user = me();
  const [view, classes, refsFile, glance, guideEntry] = await Promise.all([
    loadLessonView(nodeId),
    api<{ classes: ClassRow[] }>(user?.auth_role === "lead" ? "/api/classes" : "/api/classes?scope=own").catch(() => ({ classes: [] as ClassRow[] })),
    loadLessonRefs(),
    glanceFor(nodeId),
    // which readable guide this week has (metadata only, shared with Home); a failure only hides the link
    materialGuideCatalogue().then((list) => list.find((d) => d.node_id === nodeId) ?? null).catch(() => null),
  ]);
  if (!view) {
    page.innerHTML = `${pageHeader({ title: "Lesson not found", crumbs: [{ label: "Year plan", href: "/roadmap" }, { label: "Not found" }] })}
      ${emptyState("This week is not in the year plan. It may have moved.", `<a class="btn secondary" data-link href="/roadmap">Open the year plan</a>`)}`;
    return;
  }
  const { cur, node, mat, res, reg } = view;
  const refs = refsFile?.nodes ?? null;
  const ref = refs?.[node.id] ?? null;
  const cls = classId ? classes.classes.find((c) => c.id === classId) ?? null : null;
  const ownsClass = !!cls && (cls.teacher_id === undefined || cls.teacher_id === user?.id);
  let lessons: ClassLessonRow[] = [];
  // This week's lessons on the class that the viewer did not write (the Lead moved the class after its
  // first teacher planned on it): shown, never offered as the viewer's own. Other weeks carry no writer
  // here, so they are treated as the viewer's, as before.
  const byOthers = new Set<string>();
  if (cls) {
    try {
      const [cl, own] = await Promise.all([
        api<{ lessons: ClassLessonRow[] }>(`/api/classes/${encodeURIComponent(cls.id)}/lessons`),
        ownsClass
          ? api<{ week_no: number | null; lessons: { id: string }[] }>("/api/lessons?scope=week").catch(() => ({ week_no: null, lessons: [] as { id: string }[] }))
          : Promise.resolve({ week_no: null, lessons: [] as { id: string }[] }),
      ]);
      lessons = cl.lessons;
      if (own.week_no) {
        for (const l of lessons) if (l.week_no === own.week_no && !own.lessons.some((o) => o.id === l.lesson_id)) byOthers.add(l.lesson_id);
      }
    } catch { /* plans are a nicety here */ }
  }
  let schoolContext: WeeklyContext | null = null;
  if (cls && query.get("lesson")) {
    const { lesson } = await api<{ lesson: { id: string; class_id: string; term_id: string; week_no: number; node_id: string | null } }>(`/api/lessons/${encodeURIComponent(query.get("lesson")!)}`);
    if (lesson.class_id !== cls.id || lesson.node_id !== node.id) throw new Error("This saved lesson belongs to a different class or curriculum step. Open it from My plans.");
    schoolContext = { classId: cls.id, termId: lesson.term_id, weekNo: lesson.week_no, nodeId: node.id, lessonId: lesson.id };
  } else if (cls && query.get("term") && query.get("week")) {
    schoolContext = { classId: cls.id, termId: query.get("term")!, weekNo: Number(query.get("week")), nodeId: node.id };
  }
  let prepared: WeeklyPreparation | null = null;
  if (schoolContext) {
    try {
      prepared = await loadWeeklyPreparation(schoolContext);
    } catch {
      // A school week the term does not have (a year-plan week past the term's last) must not break the lesson page:
      // it opens as it always did, without a school week.
      schoolContext = null;
    }
  }
  if (schoolContext && prepared) {
    const week = prepared;
    lessons = week.lessons.map((l) => ({ lesson_id: l.id, plan_id: l.plan_id, week_no: week.week_no, topic: l.topic, status: l.status, node_id: l.node_id }));
    byOthers.clear();
    for (const lesson of week.lessons) if (!lesson.can_open) byOthers.add(lesson.id);
  }
  // A visit with no school week (a bookmark, an older link): the lesson's own week of the running term, when it has one,
  // so "Plan another lesson" still opens the form on the week this lesson belongs to (owner ruling A3).
  let ownWeek: { termId: string; weekNo: number } | null = null;
  if (!schoolContext && cls && ownsClass) {
    const running = await api<TermInfo | null>("/api/terms/current").catch(() => null);
    if (running && termNumber(running.term) === node.term && node.suggested_week >= 1 && node.suggested_week <= (running.term.week_count ?? 0)) {
      ownWeek = { termId: running.term.id, weekNo: node.suggested_week };
    }
  }
  // The chip follows the viewer's own plans on this lesson when there are any (the button does), else
  // the class's (a plan by the class's previous teacher), as on Home and This week.
  const state: PlanState = cls ? (() => {
    const onNode = lessons.filter((l) => l.node_id === node.id);
    const own = onNode.filter((l) => !byOthers.has(l.lesson_id));
    const pick = own.length ? own : onNode;
    return pick.length ? bestState(pick.map((l) => l.status)) : "none";
  })() : "none";

  const yearHref = cls ? `/roadmap?class=${encodeURIComponent(cls.id)}` : `/roadmap?grade=${cur.grade}`;
  // A required PDF a week names (a listening paper's picture choices) stays a file button: it opens inside EDEN.
  const required = (res?.items ?? []).filter((r) => r.required && r.href)
    .map((r) => fileButton(r.href!, cleanTitle(r.title), "pdf", ' data-file="required"')).join("");
  // The next step for this lesson: a draft → carry on with it; a sent plan → open it; nothing yet → plan it.
  // (Never "Plan this lesson" beside a draft: that starts a second plan by mistake.)
  const forNode = lessons.filter((l) => l.node_id === node.id && !byOthers.has(l.lesson_id));
  const othersOnNode = lessons.some((l) => l.node_id === node.id && byOthers.has(l.lesson_id));
  const draft = forNode.find((l) => l.status === "in_progress" || l.status === "not_started") ?? null;
  const sent = draft ? null : forNode[0] ?? null;
  const planBtn = !ownsClass
    ? ""
    : draft
      ? `<a class="btn" data-link href="${esc(lessonHref(draft))}">${icon("pencil")}<span>${t("lesson.continue")}</span></a>`
      : sent
        ? `<a class="btn secondary" data-link href="${esc(lessonHref(sent))}">${icon("eye")}<span>${t("lesson.view_plan")}</span></a>`
        : `<a class="btn${othersOnNode ? " secondary" : ""}" data-link href="${esc(planStartHref(cls!.id, node.id, ownWeek))}">${icon("pencil")}<span>${othersOnNode ? "Plan your own" : t("lesson.plan")}</span></a>`;

  // The readable guide (Term 1), in the words its contents deserve; a week without one gets no link.
  const guideQ = cls ? `?class=${encodeURIComponent(cls.id)}` : "";
  const guideWords = guideLinkLabel(guideEntry?.state);
  const guide = guideWords ? { label: guideWords, href: `/materials-guide/${encodeURIComponent(node.id)}${guideQ}` } : null;
  const essentials = essentialsHtml(node, mat, ref, glance, cls?.id ?? null, guide);
  const how = glanceHtml(glance, mat?.pack ?? null);
  // With the pack's steps on the page, the guide is one quiet link at the foot of that card.
  const guideLink = guide ? `<p class="glance-foot"><a class="linkbtn" data-link href="${esc(guide.href)}">${esc(guide.label)} ${icon("chevron", "ico-sm")}</a></p>` : "";
  page.innerHTML = `
    ${pageHeader({
      crumbs: [{ label: "Year plan", href: yearHref }, { label: cls ? cls.name : `Grade ${cur.grade}`, href: yearHref }, { label: nodeWhen(node) }],
      title: teacherTitle(node, ref),
      sub: mat?.oneliner ? esc(plainText(mat.oneliner)) : esc(gradeLine(cur)),
      actions: cls ? statusChip(state) : "",
    })}
    ${schoolContext ? `<p><a class="btn secondary" data-link href="${esc(weeklyHref(schoolContext))}">Back to this week</a></p>` : ""}
    <div class="lesson-actions">${planBtn}${planBtn ? `<span class="la-note muted small">Your plan opens beside this lesson's materials.</span>` : ""}${required}</div>
    <div class="lesson-grid">
      <div class="lesson-main">
        ${essentials || (mat ? "" : `<p class="muted">${t("lesson.no_sheet")}</p>`)}
        ${how ? how.replace(/<\/section>\s*$/, () => `${guideLink}</section>`) : ""}
        ${booksCardHtml(ref, { quiet: true })}
        ${whatToTeachHtml(cur, node, mat, reg, false, { slim: !!how, title: how ? "Words, grammar and school events" : "What to teach" })}
        ${allResourcesHtml(res, ref)}
      </div>
      <aside class="lesson-aside">
        ${plansPanelHtml(cls, node, lessons, ownsClass, byOthers, ownWeek)}
      </aside>
    </div>
    ${prevNextHtml(cur, node, cls?.id ?? null, refs)}`;
  if (schoolContext) {
    const q = weeklyQuery(schoolContext);
    const school = schoolContext;
    // Arriving from the Year plan, the school week IS this lesson's own week; Previous / Next then move the school week
    // with the lesson. Arriving from a school week (This week, Home), browsing keeps that week: the lessons are catch-up.
    const week = prepared && termNumber(prepared.term) === node.term && prepared.week_no === node.suggested_week ? prepared : null;
    page.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
      const url = new URL(link.href);
      if (/^\/(materials-studio|materials-guide)\//.test(url.pathname) || url.pathname === '/plan/start') {
        for (const [key, value] of q) if (key !== 'lesson' || url.pathname !== '/plan/start') url.searchParams.set(key, value);
        link.href = url.pathname + url.search;
      } else if (url.pathname.startsWith('/map/')) {
        // Browsing another curriculum step retains school context but never attaches the old lesson.
        url.searchParams.delete('lesson');url.searchParams.delete('node');
        const target = week ? cur.nodes.find((n) => n.id === decodeURIComponent(url.pathname.slice('/map/'.length))) : null;
        if (week && target) {
          const row = week.term.ay ? week.available_terms.find((term) => term.ay === week.term.ay && termNumber(term) === target.term) : undefined;
          if (row && target.suggested_week >= 1 && target.suggested_week <= row.week_count) {
            url.searchParams.set('term', row.id);url.searchParams.set('week', String(target.suggested_week));
          } else {
            url.searchParams.delete('term');url.searchParams.delete('week');
          }
        } else {
          url.searchParams.set('term', school.termId);url.searchParams.set('week', String(school.weekNo));
        }
        link.href = url.pathname + url.search;
      }
    });
  }
  // In-page jumps ("See the steps") scroll without changing the address: a #hash would make the router redraw.
  page.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLElement>("[data-jump]");
    if (!a) return;
    e.preventDefault();
    const target = page.querySelector<HTMLElement>(`#${CSS.escape(a.dataset.jump ?? "")}`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}
